import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, Globe } from "lucide-react";
import Logo from "./Logo";

export default function Header() {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const currentLang = (lang === "en" ? "en" : "it");
  const otherLang = currentLang === "it" ? "en" : "it";

  const switchLanguage = () => {
    // Preserve current sub-path if any, but always land on the corresponding language root
    const rest = location.pathname.replace(/^\/(it|en)/, "");
    const target = `/${otherLang}${rest || ""}`;
    i18n.changeLanguage(otherLang);
    navigate(target, { state: { langSwitch: true } });
  };

  const goAnchor = (id) => (e) => {
    e.preventDefault();
    const doScroll = () => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };
    // If not on the home route, navigate first
    if (location.pathname !== `/${currentLang}` && location.pathname !== `/${currentLang}/`) {
      navigate(`/${currentLang}#${id}`);
      setTimeout(doScroll, 60);
    } else {
      doScroll();
    }
    setOpen(false);
  };

  return (
    <header className={`ec-header ${scrolled ? "ec-header--scrolled" : ""}`}>
      <div className="ec-header-inner">
        <Link to={`/${currentLang}`} className="ec-header-brand" aria-label="Ecominiere">
          <Logo />
        </Link>

        <nav className="ec-nav ec-nav--desktop" aria-label="primary">
          <a href="#benefits" onClick={goAnchor("benefits")}>{t("nav.benefits")}</a>
          <a href="#how" onClick={goAnchor("how")}>{t("nav.how")}</a>
          <a href="#projects" onClick={goAnchor("projects")}>{t("nav.projects")}</a>
          <a href="#transparency" onClick={goAnchor("transparency")}>{t("nav.transparency")}</a>
          <a href="#contact" onClick={goAnchor("contact")}>{t("nav.contact")}</a>
        </nav>

        <div className="ec-header-actions">
          <button className="ec-lang-btn" onClick={switchLanguage} aria-label={t("nav.lang_switch")}>
            <Globe size={16} />
            <span>{otherLang.toUpperCase()}</span>
          </button>
          <a href="#contact" onClick={goAnchor("contact")} className="ec-btn ec-btn--primary ec-header-cta">
            {t("hero.cta_secondary")}
          </a>
          <button
            className="ec-hamburger"
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? t("nav.close_menu") : t("nav.open_menu")}
            aria-expanded={open}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="ec-mobile-panel" role="dialog" aria-label="menu">
          <nav className="ec-nav ec-nav--mobile">
            <a href="#benefits" onClick={goAnchor("benefits")}>{t("nav.benefits")}</a>
            <a href="#how" onClick={goAnchor("how")}>{t("nav.how")}</a>
            <a href="#projects" onClick={goAnchor("projects")}>{t("nav.projects")}</a>
            <a href="#transparency" onClick={goAnchor("transparency")}>{t("nav.transparency")}</a>
            <a href="#contact" onClick={goAnchor("contact")}>{t("nav.contact")}</a>
            <div className="ec-mobile-divider" />
            <NavLink to={`/${currentLang}/privacy`}>{t("nav.privacy")}</NavLink>
            <NavLink to={`/${currentLang}/cookies`}>{t("nav.cookies")}</NavLink>
            <NavLink to={`/${currentLang}/terms`}>{t("nav.terms")}</NavLink>
            <div className="ec-mobile-divider" />
            <button className="ec-lang-btn ec-lang-btn--mobile" onClick={switchLanguage}>
              <Globe size={16} />
              <span>{t("nav.lang_switch")}: {otherLang.toUpperCase()}</span>
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}
